import {snapshotUnavailableShort} from "../../utils/reportingSnapshotAvailability.js";
import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {
    currencyUnit,
    dividePercent,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartNumber,
    formatChartPercent,
    KpiCard,
    MetricBasis,
    MetricBasisToggle,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
    snapshotCompletenessAlert,
} from "./ReportingLab.jsx";
import {averageOrderValueAmount, calculatePnoPercent} from "../../utils/reportingMetricRatios.js";
import {isSnapshotPeriodIncomplete, snapshotComparisonValue, snapshotHasPublishedDays, snapshotPeriodValue, snapshotPublicationDisplay} from "../../utils/reportingSnapshotAvailability.js";
import {mediaSourceQualityBlocksMetrics} from "../../utils/reportingMetricPresentation.js";
import ReportingInfoTooltip from "../../components/ReportingInfoTooltip.jsx";
import ReportingSnapshotStatus from "./ReportingSnapshotStatus.jsx";
import {selectedCentreSpendAttributionStatus} from "./CentreSpendAttributionNotice.jsx";
import ReportingCostCoverageContext from "../../components/reporting/ReportingCostCoverageContext.jsx";
import {assignedMarketingChartPoint, partialProfitabilityChartPoint, partialProfitabilityMarginPercent, selectProfitabilityMetric, selectReportProfitabilityMetric} from "../../utils/reportingPartialProfitability.js";

function periodLabel(since, until) {
    return `${since.format("D. M. YYYY")} - ${until.format("D. M. YYYY")}`;
}

export function comparisonSnapshotMatchesRange(snapshot, expectedRange) {
    return Boolean(snapshot?.requestedSince && snapshot?.requestedUntil
        && snapshot.requestedSince === expectedRange?.since
        && snapshot.requestedUntil === expectedRange?.until);
}

export function comparisonSnapshotForRange(snapshot, expectedRange) {
    if (comparisonSnapshotMatchesRange(snapshot, expectedRange)) {
        return snapshot;
    }

    return {...(snapshot ?? {}), complete: false, rangeMismatch: true};
}

export function periodSnapshotStatusItems(snapshots, labels, expectedRanges = [], periods = []) {
    return (Array.isArray(snapshots) ? snapshots : [snapshots])
        .map((snapshot, index) => ({snapshot, label: labels[index], expectedRange: expectedRanges[index], mediaSourceQuality: periods[index]?.mediaSourceQuality ?? snapshot?.mediaSourceQuality}))
        .filter(({snapshot, expectedRange, mediaSourceQuality}) => isSnapshotPeriodIncomplete(snapshot)
            || snapshotPublicationDisplay(snapshot).hasMissing
            || (expectedRange !== undefined && !comparisonSnapshotMatchesRange(snapshot, expectedRange))
            || mediaSourceQualityBlocksMetrics(mediaSourceQuality))
        .map(({snapshot, label, expectedRange}) => {
            const rangeMismatch = expectedRange !== undefined
                && !comparisonSnapshotMatchesRange(snapshot, expectedRange);

            return {
                label,
                hasPublishedDays: snapshot == null ? false : snapshotHasPublishedDays(snapshot),
                ...(rangeMismatch ? {rangeMismatch: true} : {}),
            };
        });
}

export function comparisonPeriodMediaSourceQualityForStatus(snapshot, mediaSourceQuality) {
    const publicationDisplay = snapshotPublicationDisplay(snapshot);
    if (publicationDisplay.reportingExcludedDays.length === 0
        && Array.isArray(publicationDisplay.publishedDays) && publicationDisplay.publishedDays.length === 0) {
        return {applicable: false, available: true, complete: true, issues: []};
    }

    return mediaSourceQuality;
}

export function comparisonMetricBasisData(metricBasis) {
    const marketing = metricBasis === MetricBasis.Marketing;

    return {
        turnoverKey: marketing ? "orderTurnoverGross" : "orderTurnoverNet",
        profitKey: marketing ? "orderProfitGross" : "orderProfitNet",
        orderKey: marketing ? "orderAmountGross" : "orderAmountNet",
        partialProfitabilityKey: marketing ? "partialProfitabilityMarketing" : "partialProfitability",
        reportContext: marketing ? "marketing" : "period_comparison",
    };
}

function moneyDeltaPercent(current, previous) {
    return dividePercent(moneyAmount(current) - moneyAmount(previous), moneyAmount(previous));
}

function numberDeltaPercent(current, previous) {
    return dividePercent(Number(current ?? 0) - Number(previous ?? 0), Number(previous ?? 0));
}

export function percentagePointDelta(current, previous) {
    if (current === null || current === undefined || previous === null || previous === undefined
        || !Number.isFinite(Number(current)) || !Number.isFinite(Number(previous))) {
        return null;
    }

    return Number(current) - Number(previous);
}

export function comparisonMetricDelta(row, current, previous) {
    if (row.kind === "percent") return percentagePointDelta(current, previous);
    return row.kind === "money" ? moneyDeltaPercent(current, previous) : numberDeltaPercent(current, previous);
}

export function comparisonPairHasPartialData(currentPartial, previousPartial) {
    return currentPartial === true || previousPartial === true;
}

export function comparisonCanShowPartialDelta(row, current, previous, currentPartial, previousPartial) {
    return row?.sensitiveProfit === true
        && comparisonPairHasPartialData(currentPartial, previousPartial)
        && current != null
        && previous != null;
}

export function comparisonPeriodCentreSpendStatus(period, selectedCentreIds = []) {
    if (!Array.isArray(selectedCentreIds) || selectedCentreIds.length === 0) {
        return null;
    }

    return selectedCentreSpendAttributionStatus(period?.centreSpendAttribution, true);
}

export function comparisonPeriodMetricValue(value, snapshot, centreSpendStatus = null) {
    return centreSpendStatus === null ? snapshotPeriodValue(value, snapshot) : null;
}

export function comparisonPeriodProfitValue(selection, snapshot, centreSpendStatus = null) {
    if (selection?.value == null) return null;
    if (selection.assignedMarketing) return snapshotPeriodValue(selection.value, snapshot);
    return comparisonPeriodMetricValue(selection.value, snapshot, centreSpendStatus);
}

export function comparisonRowShowsCentreUnavailableStatus(row, periodId, centreSpendStatus) {
    return row?.centreDependent === true
        && centreSpendStatus !== null
        && centreSpendStatus !== undefined
        && row?.[`${periodId}AssignedMarketing`] !== true;
}

export function comparisonPeriodMetrics(period, turnoverKey, profitKey, orderKey, centreSpendStatus = null, partialProfitability = null, mediaSourceQuality = null) {
    const turnover = moneyAmount(period?.[turnoverKey]);
    const orders = Number(period?.[orderKey] ?? 0);
    const profit = period?.[profitKey] == null ? null : moneyAmount(period[profitKey]);
    const mediaQuality = period?.mediaSourceQuality ?? mediaSourceQuality;

    return {
        aov: averageOrderValueAmount(period?.[turnoverKey]?.amount, orders),
        pno: centreSpendStatus === null ? calculatePnoPercent(period?.orderSpendMarketing?.amount, period?.[turnoverKey]?.amount, mediaQuality) : null,
        margin: centreSpendStatus !== null || mediaSourceQualityBlocksMetrics(mediaQuality) ? null : partialProfitability?.available === true
            ? partialProfitabilityMarginPercent(partialProfitability)
            : turnover > 0 && profit !== null ? profit / turnover * 100 : null,
        marginPartial: centreSpendStatus === null && !mediaSourceQualityBlocksMetrics(mediaQuality) && partialProfitability?.available === true
            && partialProfitabilityMarginPercent(partialProfitability) !== null,
        marginPartialProfitability: centreSpendStatus === null && !mediaSourceQualityBlocksMetrics(mediaQuality) && partialProfitability?.available === true ? partialProfitability : null,
    };
}

export function comparisonMarginPartialMetadata(currentMetrics, previousMetrics, yearMetrics, periods) {
    return {
        currentPartial: currentMetrics.marginPartial,
        previousPartial: previousMetrics.marginPartial,
        yearPartial: yearMetrics.marginPartial,
        currentPartialProfitability: currentMetrics.marginPartialProfitability ?? periods.current?.partialProfitability,
        previousPartialProfitability: previousMetrics.marginPartialProfitability ?? periods.previous?.partialProfitability,
        yearPartialProfitability: yearMetrics.marginPartialProfitability ?? periods.year?.partialProfitability,
    };
}

export function comparisonAdMetricDelta(delta, currentValue, previousValue, currentCentreSpendStatus, previousCentreSpendStatus, currentSnapshot, previousSnapshot) {
    if (currentCentreSpendStatus !== null || previousCentreSpendStatus !== null || currentValue == null || previousValue == null) {
        return null;
    }

    return comparisonReportingDelta(delta, currentSnapshot, previousSnapshot);
}

function publicationDayPositions(snapshot) {
    const display = snapshotPublicationDisplay(snapshot);
    if (!Array.isArray(display.publishedDays) || !display.requestedSince || !display.requestedUntil) return null;
    const start = new Date(`${display.requestedSince}T12:00:00Z`);
    const end = new Date(`${display.requestedUntil}T12:00:00Z`);
    const requestedCount = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
    if (!Number.isInteger(requestedCount) || requestedCount <= 0) return null;
    const positions = display.publishedDays.map((day) => {
        const date = new Date(`${day}T12:00:00Z`);
        return Math.round((date.getTime() - start.getTime()) / 86400000);
    });
    return {requestedCount, positions};
}

export function reportingExclusionsCauseUnalignedCoverage(currentSnapshot, previousSnapshot) {
    const currentDisplay = snapshotPublicationDisplay(currentSnapshot);
    const previousDisplay = snapshotPublicationDisplay(previousSnapshot);
    if (currentDisplay.reportingExcludedDays.length === 0 && previousDisplay.reportingExcludedDays.length === 0) return false;

    const currentCoverage = publicationDayPositions(currentSnapshot);
    const previousCoverage = publicationDayPositions(previousSnapshot);
    if (!currentCoverage || !previousCoverage) return false;

    return currentCoverage.requestedCount !== previousCoverage.requestedCount
        || currentCoverage.positions.join(",") !== previousCoverage.positions.join(",");
}

export function comparisonReportingDelta(delta, currentSnapshot, previousSnapshot) {
    if (reportingExclusionsCauseUnalignedCoverage(currentSnapshot, previousSnapshot)) return null;
    return snapshotComparisonValue(delta, currentSnapshot, previousSnapshot);
}

export function comparisonMoneyChartValue(row, periodId) {
    const centreSpendStatus = row[`${periodId}CentreSpendStatus`];
    if (row.partialSpend && centreSpendStatus !== null && centreSpendStatus !== undefined) {
        return null;
    }

    return row[periodId] == null ? null : moneyToMajor(row[periodId]);
}

export function comparisonPeriodProfitSelection(period, snapshot, centreSpendStatus = null, usePartial = true, profitKey = "orderProfitNet", partialProfitability = period?.partialProfitability) {
    const summaryUnavailable = !snapshotHasPublishedDays(snapshot);
    if (mediaSourceQualityBlocksMetrics(period?.mediaSourceQuality ?? snapshot?.mediaSourceQuality)) {
        return {value: null, partial: false, estimated: false, allowed: false, assignedMarketing: false};
    }
    let standardSelection;
    if (!usePartial || centreSpendStatus !== null || summaryUnavailable) {
        standardSelection = selectProfitabilityMetric({fullValue: period?.[profitKey], fullComplete: partialProfitability === undefined && period?.[profitKey] !== undefined, partialProfitability: null, partialField: "knownProfitAfterAllMarketing"});
    } else {
        standardSelection = selectProfitabilityMetric({
            fullValue: period?.[profitKey],
            fullComplete: partialProfitability === undefined ? period?.[profitKey] !== undefined : partialProfitability.complete === true,
            partialProfitability,
            partialField: "knownProfitAfterAllMarketing",
        });
    }
    return selectReportProfitabilityMetric({
        standardSelection,
        partialProfitability: usePartial ? partialProfitability : null,
        assignedField: "profitAfterAssignedMarketing",
        snapshotIncomplete: summaryUnavailable,
    });
}

export function comparisonRowCentreAttributionContexts(row, comparisons, t) {
    if (row?.centreDependent !== true) {
        return [];
    }

    return (comparisons ?? [])
        .filter(({id}) => comparisonRowShowsCentreUnavailableStatus(row, id, row[`${id}CentreSpendStatus`]))
        .map(({id, label}) => ({
            id,
            label,
            detail: row[`${id}CentreSpendStatus`] === "unknown"
                ? t("reporting_lab.centre_spend_attribution.channel_unknown_help", {defaultValue: "Odpověď neobsahuje údaje o úplnosti přiřazení. Hodnoty mohou být částečné; další reklamní náklady nemají jednoznačné středisko. Úplný PNO a zisk po reklamě proto nelze určit."})
                : t("reporting_lab.centre_spend_attribution.channel_partial_help", {defaultValue: "Známé hodnoty přiřazené vybraným střediskům; další reklamní náklady nemají jednoznačné středisko. Úplný PNO a zisk po reklamě proto nelze určit."}),
        }));
}

function comparisonTone(value) {
    if (value == null) {
        return "default";
    }

    if (value < 0) {
        return "bad";
    }

    if (value > 0) {
        return "good";
    }

    return "default";
}

export function comparisonDeltaMeta(value, renderPercent, t, partialKnownResult = false) {
    if (value == null) {
        return t("reporting_lab.comparison.not_comparable_previous", {defaultValue: "Nelze porovnat s předchozím obdobím"});
    }

    const context = partialKnownResult
        ? t("reporting_lab.comparison.partial_known_result", {defaultValue: "dílčí známý výsledek vs předchozí období"})
        : t("reporting_lab.comparison.vs_previous", {defaultValue: "vs předchozí období"});

    return <span><strong className={`whitespace-nowrap font-semibold ${value < 0 ? "text-datixo-negative" : "text-slate-700 dark:text-slate-200"}`}>{renderPercent(value)}</strong><span className="font-normal text-slate-500 dark:text-slate-400"> {context}</span></span>;
}

export default function ReportingPeriodComparisonLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed, customerWholesaleRequestFilter} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const [periods, setPeriods] = useState(null);
    const [snapshots, setSnapshots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [metricBasis, setMetricBasis] = useState(MetricBasis.Marketing);
    const metricBasisMeta = useMemo(() => ({
        [MetricBasis.Marketing]: {
            label: t("reporting_lab.metric_basis.marketing.label", {defaultValue: "Marketingový výkon"}),
            shortLabel: t("reporting_lab.metric_basis.marketing.short_label", {defaultValue: "Bez storen"}),
            description: t("reporting_lab.metric_basis.marketing.description", {defaultValue: "Objednávky, které přišly a nejsou stornované. Vhodné pro vyhodnocení marketingu."}),
        },
        [MetricBasis.Completed]: {
            label: t("reporting_lab.metric_basis.business.label", {defaultValue: "Obchodní výkon"}),
            shortLabel: t("reporting_lab.metric_basis.business.short_label", {defaultValue: "Dokončené"}),
            description: t("reporting_lab.metric_basis.business.description", {defaultValue: "Jen dokončené objednávky. Vhodné pro majitele, finance a reálný vyfakturovaný výkon."}),
        },
    }), [t]);
    const {et, retentionExpired} = useReportingError(project.id, setLoading);

    const comparisons = useMemo(() => {
        const days = until.diff(since, "days") + 1;
        const previousUntil = since.clone().subtract(1, "day");
        const previousSince = previousUntil.clone().subtract(days - 1, "days");
        const yearSince = since.clone().subtract(1, "year");
        const yearUntil = until.clone().subtract(1, "year");

        return [
            {
                id: "current",
                label: t("reporting_lab.comparison.current_period", {defaultValue: "Aktuální období"}),
                since,
                until,
            },
            {
                id: "previous",
                label: t("reporting_lab.comparison.previous_period", {defaultValue: "Předchozí období"}),
                since: previousSince,
                until: previousUntil,
            },
            {
                id: "year",
                label: t("reporting_lab.comparison.previous_year", {defaultValue: "Minulý rok"}),
                since: yearSince,
                until: yearUntil,
            },
        ];
    }, [since, until, t]);

    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setPeriods(null);
        setSnapshots([]);

        const api = new ErpCalculationApi(getAuthorityConfiguration());

        api.reportingPeriodComparison({
            projectId: project.id,
            periodComparisonInput: {
                current: {
                    ...customerWholesaleRequestFilter,
                    since: comparisons[0].since.format("YYYY-MM-DD"),
                    until: comparisons[0].until.format("YYYY-MM-DD"),
                    selectedCentreIds: centresAllowListed,
                    selectedDomainIds: domainsAllowListed,
                },
                previous: {
                    ...customerWholesaleRequestFilter,
                    since: comparisons[1].since.format("YYYY-MM-DD"),
                    until: comparisons[1].until.format("YYYY-MM-DD"),
                    selectedCentreIds: centresAllowListed,
                    selectedDomainIds: domainsAllowListed,
                },
                previousYear: {
                    ...customerWholesaleRequestFilter,
                    since: comparisons[2].since.format("YYYY-MM-DD"),
                    until: comparisons[2].until.format("YYYY-MM-DD"),
                    selectedCentreIds: centresAllowListed,
                    selectedDomainIds: domainsAllowListed,
                },
            },
        })
            .then((nextPeriods) => {
                if (!active) {
                    return;
                }

                setPeriods({
                    current: nextPeriods.current,
                    previous: nextPeriods.previous,
                    year: nextPeriods.previousYear,
                });
                setSnapshots([
                    nextPeriods.currentSnapshot,
                    nextPeriods.previousSnapshot,
                    nextPeriods.previousYearSnapshot,
                ]);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, comparisons, centreFilterKey, domainFilterKey, customerWholesaleRequestFilter]);
    const current = periods?.current;
    const previous = periods?.previous;
    const year = periods?.year;
    const currentCentreSpendStatus = comparisonPeriodCentreSpendStatus(current, centresAllowListed);
    const previousCentreSpendStatus = comparisonPeriodCentreSpendStatus(previous, centresAllowListed);
    const yearCentreSpendStatus = comparisonPeriodCentreSpendStatus(year, centresAllowListed);
    const currentSnapshot = snapshots[0];
    const comparisonRanges = comparisons.map(({since: rangeSince, until: rangeUntil}) => ({
        since: rangeSince.format("YYYY-MM-DD"),
        until: rangeUntil.format("YYYY-MM-DD"),
    }));
    const previousSnapshot = comparisonSnapshotForRange(snapshots[1], comparisonRanges[1]);
    const yearSnapshot = comparisonSnapshotForRange(snapshots[2], comparisonRanges[2]);
    const basisData = comparisonMetricBasisData(metricBasis);
    const {turnoverKey, profitKey, orderKey} = basisData;
    const currentPartialProfitability = current?.[basisData.partialProfitabilityKey];
    const previousPartialProfitability = previous?.[basisData.partialProfitabilityKey];
    const yearPartialProfitability = year?.[basisData.partialProfitabilityKey];
    const comparisonPeriods = [current, previous, year];
    const snapshotStatusItems = periodSnapshotStatusItems(snapshots, comparisons.map(({label}) => label), comparisonRanges, comparisonPeriods);
    const hasRangeMismatchStatus = snapshotStatusItems.some(({rangeMismatch}) => rangeMismatch);
    const snapshotStatusHelp = <>
        {hasRangeMismatchStatus && <div>{t("reporting_lab.snapshot.comparison_range_unavailable_help", {defaultValue: "Srovnání se zobrazí jen tehdy, když jsou dostupná data za celé vybrané období. Uchovávaná historie může začínat později než požadované období."})}</div>}
    </>;
    const comparisonStatusSnapshots = [currentSnapshot, previousSnapshot, yearSnapshot];
    const periodPublicationStatusItems = snapshotStatusItems
        .filter(({rangeMismatch}) => !rangeMismatch)
        .map((item) => {
            const index = comparisons.findIndex(({label}) => label === item.label);
            const snapshot = comparisonStatusSnapshots[index];
            const mediaSourceQuality = comparisonPeriodMediaSourceQualityForStatus(
                snapshot,
                comparisonPeriods[index]?.mediaSourceQuality ?? snapshot?.mediaSourceQuality,
            );
            return {...item, snapshot: {...snapshot, mediaSourceQuality}, mediaSourceQuality};
        });
    const rangeMismatchStatusItems = snapshotStatusItems.filter(({rangeMismatch}) => rangeMismatch);
    const costCoveragePeriods = [
        {id: "current", comparison: comparisons[0], data: currentPartialProfitability, snapshot: currentSnapshot},
        {id: "previous", comparison: comparisons[1], data: previousPartialProfitability, snapshot: previousSnapshot},
        {id: "year", comparison: comparisons[2], data: yearPartialProfitability, snapshot: yearSnapshot},
    ].filter(({data, snapshot}) => snapshotHasPublishedDays(snapshot) && data != null)
        .map(({id, comparison, data}) => ({
            label: comparison.label,
            periodIdentity: id,
            reportContext: basisData.reportContext,
            data,
            requestFilters: {
                since: comparison.since.format("YYYY-MM-DD"),
                until: comparison.until.format("YYYY-MM-DD"),
                selectedCentreIds: centresAllowListed ?? [],
                selectedDomainIds: domainsAllowListed ?? [],
            },
        }));
    const currentAvailable = snapshotHasPublishedDays(currentSnapshot);
    const previousAvailable = snapshotHasPublishedDays(previousSnapshot);
    const yearAvailable = snapshotHasPublishedDays(yearSnapshot);
    const currentForMetrics = currentAvailable ? current : null;
    const previousForMetrics = previousAvailable ? previous : null;
    const yearForMetrics = yearAvailable ? year : null;
    const currency = current?.currency
        ?? current?.orderTurnoverGross?.currency
        ?? previous?.orderTurnoverGross?.currency
        ?? projectCurrency(project);
    const hasProfit = [current, previous, year].some((period) => period?.[profitKey] !== undefined);
    const currentProfitSelection = comparisonPeriodProfitSelection(current, currentSnapshot, currentCentreSpendStatus, true, profitKey, currentPartialProfitability);
    const previousProfitSelection = comparisonPeriodProfitSelection(previous, previousSnapshot, previousCentreSpendStatus, true, profitKey, previousPartialProfitability);
    const yearProfitSelection = comparisonPeriodProfitSelection(year, yearSnapshot, yearCentreSpendStatus, true, profitKey, yearPartialProfitability);
    const currentProfitValue = comparisonPeriodProfitValue(currentProfitSelection, currentSnapshot, currentCentreSpendStatus);
    const previousProfitValue = comparisonPeriodProfitValue(previousProfitSelection, previousSnapshot, previousCentreSpendStatus);
    const yearProfitValue = comparisonPeriodProfitValue(yearProfitSelection, yearSnapshot, yearCentreSpendStatus);
    const currentMetrics = comparisonPeriodMetrics(currentForMetrics && !currentProfitSelection.partial ? currentForMetrics : currentForMetrics ? {...currentForMetrics, [profitKey]: null} : null, turnoverKey, profitKey, orderKey, currentCentreSpendStatus, currentProfitSelection.partial && !currentProfitSelection.assignedMarketing ? currentPartialProfitability : null, currentSnapshot?.mediaSourceQuality);
    const previousMetrics = comparisonPeriodMetrics(previousForMetrics && !previousProfitSelection.partial ? previousForMetrics : previousForMetrics ? {...previousForMetrics, [profitKey]: null} : null, turnoverKey, profitKey, orderKey, previousCentreSpendStatus, previousProfitSelection.partial && !previousProfitSelection.assignedMarketing ? previousPartialProfitability : null, previousSnapshot?.mediaSourceQuality);
    const yearMetrics = comparisonPeriodMetrics(yearForMetrics && !yearProfitSelection.partial ? yearForMetrics : yearForMetrics ? {...yearForMetrics, [profitKey]: null} : null, turnoverKey, profitKey, orderKey, yearCentreSpendStatus, yearProfitSelection.partial && !yearProfitSelection.assignedMarketing ? yearPartialProfitability : null, yearSnapshot?.mediaSourceQuality);
    const showCurrentProfitCard = currentAvailable
        && currentProfitSelection.allowed
        && (currentProfitValue != null || currentCentreSpendStatus !== null);
    const profitDelta = comparisonAdMetricDelta(
        currentProfitValue == null || previousProfitValue == null ? null : moneyDeltaPercent(currentProfitValue, previousProfitValue),
        currentProfitValue,
        previousProfitValue,
        currentCentreSpendStatus,
        previousCentreSpendStatus,
        currentSnapshot,
        previousSnapshot,
    );
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const rows = [
        {
            label: t("reporting_lab.comparison.revenue", {defaultValue: "Obrat"}),
            current: snapshotPeriodValue(current?.[turnoverKey], currentSnapshot),
            previous: snapshotPeriodValue(previous?.[turnoverKey], previousSnapshot),
            year: snapshotPeriodValue(year?.[turnoverKey], yearSnapshot),
            kind: "money",
        },
        {
            label: t("reporting_lab.comparison.profit", {defaultValue: "Hrubý zisk po reklamě"}),
            current: currentProfitValue,
            previous: previousProfitValue,
            year: yearProfitValue,
            kind: "money",
            sensitiveProfit: true,
            centreDependent: true,
            currentCentreSpendStatus,
            previousCentreSpendStatus,
            yearCentreSpendStatus,
            currentPartial: currentProfitSelection.partial,
            previousPartial: previousProfitSelection.partial,
            yearPartial: yearProfitSelection.partial,
            currentAssignedMarketing: currentProfitSelection.assignedMarketing,
            previousAssignedMarketing: previousProfitSelection.assignedMarketing,
            yearAssignedMarketing: yearProfitSelection.assignedMarketing,
            currentPartialProfitability,
            previousPartialProfitability,
            yearPartialProfitability,
            currentEstimated: !currentProfitSelection.partial && currentProfitValue != null && currentPartialProfitability?.quality === "estimated" && Number(currentPartialProfitability?.estimatedLineCount) > 0,
            previousEstimated: !previousProfitSelection.partial && previousProfitValue != null && previousPartialProfitability?.quality === "estimated" && Number(previousPartialProfitability?.estimatedLineCount) > 0,
            yearEstimated: !yearProfitSelection.partial && yearProfitValue != null && yearPartialProfitability?.quality === "estimated" && Number(yearPartialProfitability?.estimatedLineCount) > 0,
        },
        {
            label: t("reporting_lab.comparison.marketing", {defaultValue: "Marketing"}),
            current: snapshotPeriodValue(current?.orderSpendMarketing, currentSnapshot),
            previous: snapshotPeriodValue(previous?.orderSpendMarketing, previousSnapshot),
            year: snapshotPeriodValue(year?.orderSpendMarketing, yearSnapshot),
            kind: "money",
            centreDependent: true,
            partialSpend: true,
            currentCentreSpendStatus,
            previousCentreSpendStatus,
            yearCentreSpendStatus,
        },
        {
            label: t("reporting_lab.comparison.orders", {defaultValue: "Objednávky"}),
            current: snapshotPeriodValue(current?.[orderKey], currentSnapshot),
            previous: snapshotPeriodValue(previous?.[orderKey], previousSnapshot),
            year: snapshotPeriodValue(year?.[orderKey], yearSnapshot),
            kind: "number",
        },
        {
            label: t("reporting_lab.orders.cancelled", {defaultValue: "Stornované"}),
            current: snapshotPeriodValue(current?.orderAmountCancelled, currentSnapshot),
            previous: snapshotPeriodValue(previous?.orderAmountCancelled, previousSnapshot),
            year: snapshotPeriodValue(year?.orderAmountCancelled, yearSnapshot),
            kind: "number",
            negativeDirection: true,
        },
        {
            label: t("reporting_lab.table.aov", {defaultValue: "AOV"}),
            current: currentMetrics.aov,
            previous: previousMetrics.aov,
            year: yearMetrics.aov,
            kind: "currencyNumber",
        },
        {
            label: t("reporting_lab.comparison.pno", {defaultValue: "PNO"}),
            current: currentMetrics.pno,
            previous: previousMetrics.pno,
            year: yearMetrics.pno,
            kind: "percent",
            negativeDirection: true,
            centreDependent: true,
            currentCentreSpendStatus,
            previousCentreSpendStatus,
            yearCentreSpendStatus,
        },
        {
            label: t("reporting_lab.comparison.margin", {defaultValue: "Marže po reklamě (%)"}),
            current: currentMetrics.margin,
            previous: previousMetrics.margin,
            year: yearMetrics.margin,
            kind: "percent",
            sensitiveProfit: true,
            centreDependent: true,
            ...comparisonMarginPartialMetadata(currentMetrics, previousMetrics, yearMetrics, {
                current: {partialProfitability: currentPartialProfitability},
                previous: {partialProfitability: previousPartialProfitability},
                year: {partialProfitability: yearPartialProfitability},
            }),
            currentCentreSpendStatus,
            previousCentreSpendStatus,
            yearCentreSpendStatus,
        },
    ];
    const visibleRows = rows.filter((row) => hasProfit || !row.sensitiveProfit);
    const moneyRows = visibleRows.filter((row) => row.kind === "money");
    const countRows = visibleRows.filter((row) => row.kind === "number");
    const renderComparisonValue = (row, value) => {
        const formattedValue = value == null
            ? "—"
            : row.kind === "money" ? renderCurrencyValue(value, true)
                : row.kind === "currencyNumber" ? renderCurrencyValue({amount: Math.round(value), currency}, true)
                    : row.kind === "percent" ? renderPercentValue(value)
                        : renderNumber(value);

        if (value == null) return "—";
        return formattedValue;
    };

    const moneyChartOption = {
        color: ["#2563eb", "#16a34a", "#f97316"],
        textStyle: chartTextStyle,
        grid: {left: 18, right: 18, top: 24, bottom: 52, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        legend: {bottom: 0, left: "center", ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: moneyRows.map((row) => row.label),
            axisLabel: {fontSize: 12},
        },
        yAxis: {
            type: "value",
            name: currencyUnit(currency),
            nameTextStyle: chartTextStyle,
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        series: comparisons.map((comparison) => ({
            name: comparison.label,
            type: "bar",
            data: moneyRows.map((row) => row[`${comparison.id}AssignedMarketing`]
                ? assignedMarketingChartPoint(comparisonMoneyChartValue(row, comparison.id), row[`${comparison.id}PartialProfitability`], t)
                : partialProfitabilityChartPoint(comparisonMoneyChartValue(row, comparison.id), row[`${comparison.id}PartialProfitability`], row[`${comparison.id}Partial`] === true, t)),
            barMaxWidth: 30,
        })),
    };

    const countChartOption = {
        color: ["#2563eb", "#16a34a", "#f97316"],
        textStyle: chartTextStyle,
        grid: {left: 18, right: 18, top: 24, bottom: 52, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, formatChartNumber),
        },
        legend: {bottom: 0, left: "center", ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: countRows.map((row) => row.label),
            axisLabel: {fontSize: 12},
        },
        yAxis: {
            type: "value",
            name: t("reporting_lab.comparison.orders", {defaultValue: "Objednávky"}),
            nameTextStyle: chartTextStyle,
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        series: comparisons.map((comparison) => ({
            name: comparison.label,
            type: "bar",
            data: countRows.map((row) => row[comparison.id] == null ? null : Number(row[comparison.id])),
            barMaxWidth: 30,
        })),
    };

    const marginOption = {
        color: ["#16a34a", "#f97316", "#7c3aed"],
        textStyle: chartTextStyle,
        grid: {left: 18, right: 18, top: 24, bottom: 52, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartPercent(value, 1)),
        },
        legend: {bottom: 0, left: "center", ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: comparisons.map((comparison) => comparison.label),
            axisLabel: {fontSize: 12},
        },
        yAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: (value) => formatChartPercent(value)},
        },
        series: [
            {
                name: t("reporting_lab.comparison.margin", {defaultValue: "Marže po reklamě (%)"}),
                type: "bar",
                data: [currentMetrics, previousMetrics, yearMetrics].map((metrics, index) => partialProfitabilityChartPoint(metrics.margin, [currentPartialProfitability, previousPartialProfitability, yearPartialProfitability][index], metrics.marginPartial, t)),
                barMaxWidth: 34,
            },
            {
                name: t("reporting_lab.comparison.pno", {defaultValue: "PNO"}),
                type: "bar",
                data: [currentMetrics.pno, previousMetrics.pno, yearMetrics.pno],
                barMaxWidth: 34,
            },
        ].filter((series) => hasProfit || series.name !== t("reporting_lab.comparison.margin", {defaultValue: "Marže po reklamě (%)"})),
    };

    const revenueDelta = moneyDeltaPercent(current?.[turnoverKey], previous?.[turnoverKey]);
    const orderDelta = numberDeltaPercent(current?.[orderKey], previous?.[orderKey]);

    return <WideSecureLayout periodReport retentionExpired={retentionExpired} comparisonRetentionExpired={retentionExpired}
        title={t("reporting_lab.pages.period_comparison", {defaultValue: "Srovnání období"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
        alerts={[snapshotCompletenessAlert(snapshots, t)]}
     intro={<ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.period_comparison.title", {defaultValue: "K čemu slouží srovnání období"})}
                    actions={<MetricBasisToggle value={metricBasis} onChange={setMetricBasis} metricBasisMeta={metricBasisMeta}/>}
                >
                    {t("reporting_lab.descriptions.period_comparison.text", {defaultValue: "Porovnává zvolenou bázi výkonu v aktuálním období s předchozím stejně dlouhým obdobím a stejným datumovým oknem před rokem."})}
                </ReportingPageDescription>}>
        <div className="grid grid-cols-12 gap-4">
                {rangeMismatchStatusItems.length > 0 && <div role="note" className="col-span-12 flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                    {rangeMismatchStatusItems.map((item) => <span key={item.label}>{item.label}: {t("reporting_lab.snapshot.comparison_range_unavailable_short", {defaultValue: "Srovnání za celé období není dostupné"})}</span>)}
                    <ReportingInfoTooltip>{snapshotStatusHelp}</ReportingInfoTooltip>
                </div>}
                {periodPublicationStatusItems.map(({label, snapshot, mediaSourceQuality}) => <ReportingSnapshotStatus key={label} snapshots={snapshot} label={label} mediaSourceQuality={mediaSourceQuality}/>)}
                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.comparison.revenue", {defaultValue: "Obrat"})} value={renderCurrencyValue(snapshotPeriodValue(current?.[turnoverKey], currentSnapshot), true)} meta={comparisonDeltaMeta(comparisonReportingDelta(revenueDelta, currentSnapshot, previousSnapshot), renderPercentValue, t)} tone={comparisonTone(comparisonReportingDelta(revenueDelta, currentSnapshot, previousSnapshot))}/>
                </div>
                {showCurrentProfitCard && <div className="col-span-12 md:col-span-4">
                    <KpiCard
                        loading={loading}
                        label={currentProfitSelection.partial ? t("reporting_lab.kpi.partial_profit", {defaultValue: "Dílčí hrubý zisk po reklamě"}) : t("reporting_lab.comparison.profit", {defaultValue: "Hrubý zisk po reklamě"})}
                        value={currentProfitValue == null ? "—" : renderCurrencyValue(currentProfitValue, true)}
                        valueClassName={currentProfitValue == null && currentCentreSpendStatus !== null ? "text-slate-500 dark:text-slate-400" : ""}
                        meta={currentProfitValue != null
                            ? comparisonDeltaMeta(profitDelta, renderPercentValue, t, currentProfitSelection.partial || previousProfitSelection.partial)
                            : currentCentreSpendStatus !== null ? t("reporting_lab.cost_quality.profit_unavailable_short", {defaultValue: "Není dostupný"}) : snapshotUnavailableShort(currentSnapshot, t)}
                        metaTone={currentCentreSpendStatus !== null && !currentProfitSelection.assignedMarketing ? "unavailable" : "default"}
                        info={<>
                            <div>{t("reporting_lab.table.contribution_help", {defaultValue: "Hrubý zisk po reklamě = hrubý zisk minus náklady na reklamní média. Jde o částku před dalšími variabilními náklady a režií."})}</div>
                            {currentProfitSelection.partial && <div className="mt-1">{t("reporting_lab.partial_profitability.help", {defaultValue: "Výsledek vychází z řádků se známou tržbou a nákupním nákladem. Od jejich hrubého zisku se odečítají všechny reklamní náklady stejného výběru."})}</div>}
                            {currentProfitSelection.assignedMarketing && <div className="mt-1">{t("reporting_lab.partial_profitability.assigned_profit_help", {defaultValue: "Výsledek po reklamě vychází z přiřazených nákladů a uvedeného základu zisku. Nepřiřazené náklady se do výpočtu nezahrnují." , basis: currentPartialProfitability?.assignedMarketingResult?.grossProfitBasis === "full" ? t("reporting_lab.partial_profitability.assigned_basis_full", {defaultValue: "úplného hrubého zisku"}) : t("reporting_lab.partial_profitability.assigned_basis_known_lines", {defaultValue: "hrubého zisku známých řádků"}), amount: renderCurrencyValue(currentPartialProfitability?.assignedMarketingResult?.assignedMarketingSpend, true)})}</div>}
                            {currentCentreSpendStatus !== null && !currentProfitSelection.assignedMarketing && <div className="mt-1">{currentCentreSpendStatus === "unknown"
                                ? t("reporting_lab.centre_spend_attribution.channel_unknown_help", {defaultValue: "Odpověď neobsahuje údaje o úplnosti přiřazení. Hodnoty mohou být částečné; další reklamní náklady nemají jednoznačné středisko. Úplný PNO a zisk po reklamě proto nelze určit."})
                                : t("reporting_lab.centre_spend_attribution.channel_partial_help", {defaultValue: "Známé hodnoty přiřazené vybraným střediskům; další reklamní náklady nemají jednoznačné středisko. Úplný PNO a zisk po reklamě proto nelze určit."})}</div>}
                        </>}
                        infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}
                        tone={comparisonTone(profitDelta)}
                    />
                </div>}
                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.comparison.orders", {defaultValue: "Objednávky"})} value={renderNumber(snapshotPeriodValue(current?.[orderKey], currentSnapshot))} meta={comparisonDeltaMeta(comparisonReportingDelta(orderDelta, currentSnapshot, previousSnapshot), renderPercentValue, t)} tone={comparisonTone(comparisonReportingDelta(orderDelta, currentSnapshot, previousSnapshot))}/>
                </div>

                <Panel loading={loading} title={t("reporting_lab.comparison.periods", {defaultValue: "Porovnávaná období"})} className="col-span-12">
                    <div className="grid divide-y divide-slate-200 dark:divide-slate-700 min-[900px]:grid-cols-3 min-[900px]:divide-x min-[900px]:divide-y-0">
                        {comparisons.map((comparison, index) => {
                            const coveragePeriod = costCoveragePeriods.find(({periodIdentity}) => periodIdentity === comparison.id);
                            const spacing = index === 0 ? "px-2 py-2 min-[900px]:pr-4" : index === 1 ? "px-2 py-2 min-[900px]:px-4" : "px-2 py-2 min-[900px]:pl-4";
                            return <div key={comparison.id} className={`flex min-w-0 flex-col max-[480px]:px-0 ${spacing}`}>
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{comparison.label}</div>
                                <div className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{periodLabel(comparison.since, comparison.until)}</div>
                                {coveragePeriod && <div className="mt-3 flex flex-1 flex-col">
                                    <ReportingCostCoverageContext periods={[{...coveragePeriod, label: null}]} reportContext={basisData.reportContext} comparisonLayout={true} loading={loading}/>
                                </div>}
                            </div>;
                        })}
                    </div>
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.comparison.financial_comparison", {defaultValue: "Finanční srovnání"})} className="col-span-12 xl:col-span-8">
                    <EChart option={moneyChartOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.comparison.order_comparison", {defaultValue: "Objednávky a storna"})} className="col-span-12 xl:col-span-4">
                    <EChart option={countChartOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.comparison.efficiency_comparison", {defaultValue: "Efektivita"})} className="col-span-12">
                    <EChart option={marginOption} className="h-96"/>
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.comparison.table", {defaultValue: "Tabulka srovnání"})} className="col-span-12">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.comparison.metric", {defaultValue: "Metrika"})}</th>
                                {comparisons.map((comparison) => <th key={comparison.id} className="py-2 pr-4 text-right font-semibold">{comparison.label}</th>)}
                                <th className="py-2 text-right font-semibold">{t("reporting_lab.comparison.change", {defaultValue: "Změna"})}</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {visibleRows.map((row) => {
                                const hasPartialComparison = comparisonPairHasPartialData(row.currentPartial, row.previousPartial);
                                const centreAttributionContexts = comparisonRowCentreAttributionContexts(row, comparisons, t);
                                const rawDelta = hasPartialComparison && !comparisonCanShowPartialDelta(row, row.current, row.previous, row.currentPartial, row.previousPartial)
                                    ? null
                                    : comparisonMetricDelta(row, row.current, row.previous);
                                const delta = row.centreDependent
                                    ? comparisonAdMetricDelta(rawDelta, row.current, row.previous, row.currentCentreSpendStatus, row.previousCentreSpendStatus, currentSnapshot, previousSnapshot)
                                    : comparisonReportingDelta(rawDelta, currentSnapshot, previousSnapshot);

                                return <tr key={row.label}>
                                    <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100"><span className="inline-flex items-center gap-1.5">{row.label}{centreAttributionContexts.length > 0 && <ReportingInfoTooltip ariaLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}>{centreAttributionContexts.map(({id, label, detail}) => <div key={id}>{label}: {detail}</div>)}</ReportingInfoTooltip>}</span></td>
                                    <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderComparisonValue(row, row.current)}</td>
                                    <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderComparisonValue(row, row.previous)}</td>
                                    <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderComparisonValue(row, row.year)}</td>
                                    <td className={`py-3 text-right font-semibold ${delta == null ? "text-slate-500" : row.negativeDirection ? (delta > 0 ? "text-datixo-negative" : "text-emerald-600") : (delta < 0 ? "text-datixo-negative" : "text-emerald-600")}`}>
                                        {delta == null
                                            ? t("reporting_lab.comparison.not_comparable", {defaultValue: "Nelze porovnat"})
                                            : row.kind === "percent"
                                                ? `${delta > 0 ? "+" : ""}${formatChartNumber(delta, {maximumFractionDigits: 1})} ${t("reporting_lab.pno.percentage_point_unit", {defaultValue: "p. b."})}`
                                                : renderPercentValue(delta)}
                                        {delta != null && row.sensitiveProfit && hasPartialComparison
                                            && <span className="ml-1 block text-xs font-normal text-slate-500">{t("reporting_lab.comparison.partial_known_result", {defaultValue: "dílčí známý výsledek vs předchozí období"})}</span>}
                                    </td>
                                </tr>;
                            })}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            </div>
    </WideSecureLayout>;
}
